import React, { useEffect, useRef, useState } from 'react'
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerBody,
  DrawerCloseButton,
  Button,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  Text,
  useToast,
  Box
} from '@chakra-ui/react'
import { useIntl } from 'react-intl'
import { type Medication, type Prescription } from './types'
import MedicationRow from './MedicationRow'
import PrescriptionPreview from './PrescriptionPreview'
import { generatePrescriptionPDF } from './pdfUtils'
import {
  createEmptyMedication,
  createNewPrescription,
  extractMedicationsFromVisit
} from './prescriptionUtils'
import { prescriptionModalMessages } from './messages'

interface PrescriptionModalProps {
  isOpen: boolean
  onClose: () => void
  patient: any
  lastVisit: any
}

const PrescriptionModal: React.FC<PrescriptionModalProps> = ({
  isOpen,
  onClose,
  patient,
  lastVisit
}) => {
  const [prescription, setPrescription] = useState<Prescription>({
    id: '',
    date: '',
    patientId: '',
    patientName: '',
    medications: [],
    doctorNotes: ''
  })

  const [activeTab, setActiveTab] = useState(0)
  const toast = useToast()
  const prescriptionRef = useRef<HTMLDivElement>(null)
  const { formatMessage, locale } = useIntl()

  // Initialize prescription when modal opens
  useEffect(() => {
    if (isOpen && patient) {
      const medications = lastVisit
        ? extractMedicationsFromVisit(lastVisit)
        : []
      const newPrescription = createNewPrescription(
        patient.id || '',
        patient.name || '',
        medications
      )
      setPrescription(newPrescription)
    }
  }, [isOpen, patient, lastVisit])

  const handleAddMedication = () => {
    setPrescription({
      ...prescription,
      medications: [...prescription.medications, createEmptyMedication()]
    })
  }

  const handleUpdateMedication = (
    index: number,
    updatedMedication: Medication
  ) => {
    const updatedMedications = [...prescription.medications]
    updatedMedications[index] = updatedMedication
    setPrescription({
      ...prescription,
      medications: updatedMedications
    })
  }

  const handleRemoveMedication = (index: number) => {
    const updatedMedications = [...prescription.medications]
    updatedMedications.splice(index, 1)
    setPrescription({
      ...prescription,
      medications:
        updatedMedications.length > 0
          ? updatedMedications
          : [createEmptyMedication()]
    })
  }

  const handleDoctorNotesChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setPrescription({
      ...prescription,
      doctorNotes: e.target.value
    })
  }

  const handleGeneratePDF = async () => {
    try {
      await generatePrescriptionPDF(prescriptionRef, prescription)
      toast({
        title: formatMessage(prescriptionModalMessages.pdfGeneratedTitle),
        description: formatMessage(
          prescriptionModalMessages.pdfGeneratedDescription
        ),
        status: 'success',
        duration: 5000,
        isClosable: true
      })
    } catch (error) {
      toast({
        title: formatMessage(prescriptionModalMessages.errorTitle),
        description: formatMessage(
          prescriptionModalMessages.pdfErrorDescription
        ),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const isPreviewDisabled = prescription.medications.some((med) => !med.name)

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      placement={locale.startsWith('ar') ? 'left' : 'right'}
    >
      <DrawerOverlay backdropFilter="blur(2px)" />
      <DrawerContent>
        <DrawerHeader borderBottom="1px solid" borderColor="gray.200">
          {formatMessage(prescriptionModalMessages.createTitle)}
        </DrawerHeader>
        <DrawerCloseButton />

        <DrawerBody overflowY="auto" p={0}>
          <Tabs index={activeTab} onChange={setActiveTab}>
            <TabList>
              <Tab>{formatMessage(prescriptionModalMessages.editTab)}</Tab>
              <Tab isDisabled={isPreviewDisabled}>
                {formatMessage(prescriptionModalMessages.previewTab)}
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Text fontWeight="bold">
                    Patient: {prescription.patientName}
                  </Text>

                  {prescription.medications.map((medication, index) => (
                    <MedicationRow
                      key={medication.id}
                      medication={medication}
                      index={index}
                      onUpdate={(updatedMedication) => {
                        handleUpdateMedication(index, updatedMedication)
                      }}
                      onDelete={() => {
                        handleRemoveMedication(index)
                      }}
                    />
                  ))}

                  <Button
                    colorScheme="gray"
                    size="sm"
                    onClick={handleAddMedication}
                  >
                    {formatMessage(prescriptionModalMessages.addMedication)}
                  </Button>

                  <Box mt={4}>
                    <Text fontWeight="bold" mb={2}>
                      Doctor&apos;s Notes
                    </Text>
                    <Textarea
                      value={prescription.doctorNotes ?? ''}
                      onChange={handleDoctorNotesChange}
                      placeholder={formatMessage(
                        prescriptionModalMessages.additionalNotesPlaceholder
                      )}
                      rows={3}
                    />
                  </Box>
                </VStack>
              </TabPanel>

              <TabPanel>
                <PrescriptionPreview
                  ref={prescriptionRef}
                  prescription={prescription}
                  doctorName="Abdellatif Elgayar" // Replace with actual doctor name from context
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </DrawerBody>

        <DrawerFooter borderTop="1px solid" borderColor="gray.200">
          <Button variant="ghost" mr={3} onClick={onClose}>
            {formatMessage(prescriptionModalMessages.close)}
          </Button>
          {activeTab === 0 && (
            <Button
              colorScheme="gray"
              onClick={() => {
                setActiveTab(1)
              }}
              isDisabled={isPreviewDisabled}
            >
              {formatMessage(prescriptionModalMessages.previewButton)}
            </Button>
          )}
          {activeTab === 1 && (
            <Button
              colorScheme="gray"
              onClick={handleGeneratePDF}
              leftIcon={<span>📄</span>}
            >
              {formatMessage(prescriptionModalMessages.generatePdf)}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default PrescriptionModal
